import {
  doc, getDoc, setDoc, updateDoc, collection,
  query, orderBy, getDocs, serverTimestamp, increment, deleteDoc
} from 'firebase/firestore';
import { db } from '../firebase/config';

/**
 * Create new session in Firestore
 */
export const createSession = async (userId, title = "New Exploration") => {
  const sessionId = `session-${Date.now()}`;
  const sessionRef = doc(db, 'users', userId, 'sessions', sessionId);

  await setDoc(sessionRef, {
    sessionId,
    title,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    nodeCount: 0,
    interactionCount: 0,
    nodes: {}
  });

  console.log(`✅ Created session: ${sessionId}`);
  return sessionId;
};

/**
 * Save node to Firestore (1 WRITE - nodes as map)
 */
export const saveNode = async (userId, sessionId, nodeData) => {
  const sessionRef = doc(db, 'users', userId, 'sessions', sessionId);

  try {
    await updateDoc(sessionRef, {
      [`nodes.${nodeData.id}`]: {
        nodeId: nodeData.id,
        parentId: nodeData.data?.parentId || null,
        question: nodeData.data?.question || '',
        response: nodeData.data?.response || '',
        actions: nodeData.data?.actions || [],
        booksMentioned: nodeData.data?.booksMentioned || [],
        versesReferenced: nodeData.data?.versesReferenced || [],
        position: nodeData.position || { x: 0, y: 0 },
        timestamp: serverTimestamp(),
        // Save metadata for proper reconstruction
        isSummary: nodeData.data?.isSummary || false,
        isReflection: nodeData.data?.isReflection || false,
        isReflectAndPray: nodeData.data?.isReflectAndPray || false,
        isExplore: nodeData.data?.isExplore || false,
        isContinueJourney: nodeData.data?.isContinueJourney || false,
        isFirstNode: nodeData.data?.isFirstNode || false,
        isLastNode: nodeData.data?.isLastNode || false,
        sequenceNumber: nodeData.data?.sequenceNumber || null
      },
      nodeCount: increment(1),
      interactionCount: increment(1),
      updatedAt: serverTimestamp()
    });

    console.log(`✅ Saved node: ${nodeData.id} to session ${sessionId}`);
  } catch (error) {
    // If updateDoc fails (document doesn't exist), create it with setDoc
    if (error.code === 'not-found') {
      console.warn(`⚠️ Session ${sessionId} not found, creating it now...`);
      await setDoc(sessionRef, {
        sessionId,
        title: 'Recovered Session',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        nodeCount: 1,
        interactionCount: 1,
        nodes: {
          [nodeData.id]: {
            nodeId: nodeData.id,
            parentId: nodeData.data?.parentId || null,
            question: nodeData.data?.question || '',
            response: nodeData.data?.response || '',
            actions: nodeData.data?.actions || [],
            booksMentioned: nodeData.data?.booksMentioned || [],
            versesReferenced: nodeData.data?.versesReferenced || [],
            position: nodeData.position || { x: 0, y: 0 },
            timestamp: serverTimestamp(),
            isSummary: nodeData.data?.isSummary || false,
            isReflection: nodeData.data?.isReflection || false,
            isReflectAndPray: nodeData.data?.isReflectAndPray || false,
            isExplore: nodeData.data?.isExplore || false,
            isContinueJourney: nodeData.data?.isContinueJourney || false,
            isFirstNode: nodeData.data?.isFirstNode || false,
            isLastNode: nodeData.data?.isLastNode || false,
            sequenceNumber: nodeData.data?.sequenceNumber || null
          }
        }
      });
      console.log(`✅ Created session ${sessionId} with first node ${nodeData.id}`);
    } else {
      // Re-throw other errors
      throw error;
    }
  }
};

/**
 * Load session from Firestore (1 READ - all nodes in one call)
 */
export const loadSession = async (userId, sessionId) => {
  const sessionRef = doc(db, 'users', userId, 'sessions', sessionId);
  const sessionSnap = await getDoc(sessionRef);

  if (!sessionSnap.exists()) {
    console.warn(`⚠️ Session not found: ${sessionId}`);
    return null;
  }

  const data = sessionSnap.data();
  const nodesMap = data.nodes || {};

  // Convert nodes map to ReactFlow format
  const nodes = Object.values(nodesMap).map(node => ({
    id: node.nodeId,
    type: 'responseNode',
    position: node.position,
    data: {
      question: node.question,
      response: node.response,
      actions: node.actions,
      parentId: node.parentId
    }
  }));

  // Build edges from parentId relationships
  const edges = nodes
    .filter(node => node.data.parentId)
    .map(node => ({
      id: `edge-${node.data.parentId}-${node.id}`,
      source: node.data.parentId,
      target: node.id,
      animated: true
    }));

  console.log(`✅ Loaded session: ${sessionId} (${nodes.length} nodes)`);

  return {
    sessionId: data.sessionId,
    title: data.title,
    nodeCount: data.nodeCount,
    interactionCount: data.interactionCount,
    nodes,
    edges
  };
};

/**
 * List all user sessions
 */
export const listSessions = async (userId) => {
  const sessionsRef = collection(db, 'users', userId, 'sessions');
  const q = query(sessionsRef, orderBy('updatedAt', 'desc'));
  const snap = await getDocs(q);

  const sessions = snap.docs.map(doc => doc.data());
  console.log(`✅ Loaded ${sessions.length} sessions`);
  return sessions;
};

/**
 * Get all user sessions formatted as workflows for UI compatibility
 */
export const getAllWorkflowsFromFirestore = async (userId) => {
  if (!userId) {
    console.warn('⚠️ No userId provided for getAllWorkflowsFromFirestore');
    return [];
  }

  try {
    const sessionsRef = collection(db, 'users', userId, 'sessions');
    const q = query(sessionsRef, orderBy('updatedAt', 'desc'));
    const snap = await getDocs(q);

    const workflows = snap.docs.map(doc => {
      const data = doc.data();
      const nodesMap = data.nodes || {};

      // Convert nodes map to array
      const nodesArray = Object.values(nodesMap);

      // Calculate biblical metadata
      const allBooks = new Set();
      const allVerses = new Set();

      nodesArray.forEach(node => {
        if (node.booksMentioned) {
          node.booksMentioned.forEach(book => allBooks.add(book));
        }
        if (node.versesReferenced) {
          node.versesReferenced.forEach(verse => allVerses.add(verse));
        }
      });

      // Convert Firestore session to workflow format
      return {
        id: data.sessionId,
        name: data.title,
        timestamp: data.createdAt?.toMillis ? data.createdAt.toMillis() : Date.now(),
        createdAt: data.createdAt?.toDate ? data.createdAt.toDate().toISOString() : new Date().toISOString(),
        updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate().toISOString() : new Date().toISOString(),
        nodeCount: data.nodeCount || 0,
        interactionCount: data.interactionCount || 0,
        booksCount: allBooks.size,
        versesCount: allVerses.size,
        books: Array.from(allBooks),
        verses: Array.from(allVerses),
        // Store raw nodes for loading
        _firestoreNodes: nodesMap
      };
    });

    console.log(`✅ Loaded ${workflows.length} workflows from Firestore`);
    return workflows;
  } catch (error) {
    console.error('❌ Error loading workflows from Firestore:', error);
    return [];
  }
};

/**
 * Load a specific session from Firestore and convert to ReactFlow format
 */
export const loadWorkflowFromFirestore = async (userId, sessionId) => {
  if (!userId || !sessionId) {
    throw new Error('userId and sessionId are required');
  }

  try {
    const sessionRef = doc(db, 'users', userId, 'sessions', sessionId);
    const sessionSnap = await getDoc(sessionRef);

    if (!sessionSnap.exists()) {
      console.warn(`⚠️ Session not found: ${sessionId}`);
      return null;
    }

    const data = sessionSnap.data();
    const nodesMap = data.nodes || {};

    console.log('🔍 DEBUG: Loading session data');
    console.log('  Session ID:', sessionId);
    console.log('  nodeCount from Firestore:', data.nodeCount);
    console.log('  Nodes in map:', Object.keys(nodesMap).length);
    console.log('  Node IDs:', Object.keys(nodesMap));
    console.log('  Full nodesMap:', nodesMap);

    // Convert nodes map to ReactFlow format
    const nodes = Object.values(nodesMap).map(node => {
      console.log('  Converting node:', node.nodeId, '- has data:', {
        hasQuestion: !!node.question,
        hasResponse: !!node.response,
        hasPosition: !!node.position,
        position: node.position
      });

      return {
        id: node.nodeId,
        type: 'responseNode',
        position: node.position || { x: 0, y: 0 },
        data: {
          question: node.question || '',
          response: node.response || '',
          actions: node.actions || [],
          parentId: node.parentId || null,
          booksMentioned: node.booksMentioned || [],
          versesReferenced: node.versesReferenced || [],
          // Preserve other metadata
          nodeId: node.nodeId,
          isSummary: node.isSummary,
          isReflection: node.isReflection,
          isReflectAndPray: node.isReflectAndPray,
          isExplore: node.isExplore,
          isContinueJourney: node.isContinueJourney,
          isFirstNode: node.isFirstNode,
          isLastNode: node.isLastNode,
          sequenceNumber: node.sequenceNumber,
          // Load customization data
          customColor: node.customColor || null,
          isExpanded: node.isExpanded
        },
        style: {
          background: 'transparent',
          border: 'none'
        },
        dragHandle: '.drag-handle',
        zIndex: 1
      };
    });

    console.log('✅ Converted nodes:', nodes.length);
    console.log('  Node details:', nodes.map(n => ({ id: n.id, position: n.position, hasQuestion: !!n.data.question })));

    // Build edges from parentId relationships
    const edges = nodes
      .filter(node => node.data.parentId)
      .map(node => ({
        id: `edge-${node.data.parentId}-${node.id}`,
        source: node.data.parentId,
        target: node.id,
        type: 'smoothstep',
        animated: true,
        style: {
          stroke: '#d4af37',
          strokeWidth: '2',
          filter: 'drop-shadow(0 2px 4px rgba(212, 175, 55, 0.3))'
        },
        markerEnd: {
          type: 'arrowclosed',
          color: '#d4af37',
          width: 20,
          height: 20
        }
      }));

    console.log(`✅ Loaded session from Firestore: ${sessionId} (${nodes.length} nodes, ${edges.length} edges)`);
    console.log('🔍 Returning workflow data:', {
      sessionId: data.sessionId,
      title: data.title,
      nodeCount: data.nodeCount,
      actualNodesLength: nodes.length,
      nodesMatch: data.nodeCount === nodes.length
    });

    return {
      sessionId: data.sessionId,
      title: data.title,
      nodeCount: data.nodeCount,
      interactionCount: data.interactionCount,
      nodes,
      edges,
      nodeIdCounter: nodes.length + 1
    };
  } catch (error) {
    console.error('❌ Error loading workflow from Firestore:', error);
    throw error;
  }
};

/**
 * Update all nodes in a session (used for position updates, etc.)
 */
export const updateSessionNodes = async (userId, sessionId, nodes) => {
  if (!userId || !sessionId || !nodes) {
    throw new Error('userId, sessionId, and nodes are required');
  }

  try {
    const sessionRef = doc(db, 'users', userId, 'sessions', sessionId);

    // Build the update object with all nodes (position AND data like customColor)
    const nodesUpdate = {};
    nodes.forEach(node => {
      nodesUpdate[`nodes.${node.id}.position`] = node.position;
      // Also save customColor if it exists
      if (node.data?.customColor) {
        nodesUpdate[`nodes.${node.id}.customColor`] = node.data.customColor;
      }
      // Also save expanded state if it exists
      if (node.data?.isExpanded !== undefined) {
        nodesUpdate[`nodes.${node.id}.isExpanded`] = node.data.isExpanded;
      }
    });

    // Add updatedAt timestamp
    nodesUpdate.updatedAt = serverTimestamp();

    await updateDoc(sessionRef, nodesUpdate);
    console.log(`✅ Updated ${nodes.length} node positions and data in session ${sessionId}`);
    return true;
  } catch (error) {
    console.error('❌ Error updating session nodes:', error);
    throw error;
  }
};

/**
 * Delete a session from Firestore
 */
export const deleteWorkflowFromFirestore = async (userId, sessionId) => {
  console.log('🗑️ DELETE REQUEST INITIATED');
  console.log('  ├─ userId:', userId);
  console.log('  ├─ sessionId:', sessionId);
  console.log('  └─ path: users/' + userId + '/sessions/' + sessionId);

  if (!userId || !sessionId) {
    const error = new Error('userId and sessionId are required');
    console.error('❌ DELETE FAILED: Missing parameters', { userId, sessionId });
    throw error;
  }

  try {
    const sessionRef = doc(db, 'users', userId, 'sessions', sessionId);

    // Check if document exists before deleting
    const docSnap = await getDoc(sessionRef);
    if (!docSnap.exists()) {
      console.warn('⚠️ Document does not exist:', sessionId);
      throw new Error('Session not found in Firestore');
    }

    console.log('📄 Document exists, proceeding with delete...');
    await deleteDoc(sessionRef);
    console.log('✅ DELETED SUCCESSFULLY from Firestore:', sessionId);
    return true;
  } catch (error) {
    console.error('❌ DELETE FAILED:', {
      code: error.code,
      message: error.message,
      details: error
    });
    throw error;
  }
};
